/* istanbul ignore file */
import { mainApi } from "config";
import { getClientFor } from "infrastructure/http";
import { ILocale, ILocalizable, commonMessages } from "infrastructure/localization";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";
import { IIndustry } from "ui/shared/models/common";

import {
  IPlexTranslationMessagesGetRequest,
  IPlexTranslationMessageSaveRequest,
  IPlexTranslationsApproveRequest,
  IPlexTranslationsRequestRequest,
  IPlexTranslationsProposeRequest,
  IPlexTranslationsRequestAccountsRequest,
  IPlexTranslationsObsoleteRequest,
  IPlexTranslationsReassignRequest,
  IPlexTranslationsRejectRequest,
  IPlexTranslationsReviewRequest
} from "./PlexTranslationMessages.api.types";
import { IPlexTranslationMessageItem } from "./PlexTranslationMessages.state";

const getApiProvider = () => {
  return {
    getIndustries: async (filter?: string) => {
      const response = await getClientFor(mainApi.name).get<IIndustry[]>("/industries", { params: { filter: filter } });
      return response.data;
    },
    getLocales: async (filter?: string) => {
      const response = await getClientFor(mainApi.name).get<ILocale[]>("/locales", { params: { filter: filter } });
      return response.data;
    },
    getTranslationStatuses: () => {
      const statuses: ILocalizable[] = [
        { messageId: displayMessages.option("translationStatusInUse") },
        { messageId: displayMessages.option("translationStatusProposed") },
        { messageId: displayMessages.option("translationStatusRequested") },
        { messageId: displayMessages.option("translationStatusNoStatus") }
      ];
      return statuses;
    },
    getParentOptions: () => {
      const parentOptions: ILocalizable[] = [{ messageId: commonMessages.option("yes") }, { messageId: commonMessages.option("no") }];
      return parentOptions;
    },
    getPlexTranslationMessages: async (parameters: IPlexTranslationMessagesGetRequest) => {
      const params = {
        locale: parameters.locale,
        industry: parameters.industry,
        text: parameters.text,
        messageId: parameters.messageId,
        status: parameters.status,
        assignedTo: parameters.assignedTo,
        parent: parameters.parent
      };

      const response = await getClientFor(mainApi.name).get<IPlexTranslationMessageItem[]>("/messages/plex-translations/search", { params });

      return response.data;
    },

    getPlexAccounts: async (request: IPlexTranslationsRequestAccountsRequest) => {
      const response = await getClientFor(mainApi.name).get(`/security-roles/${request.securityRoleId}/tenants/${request.tenantId}/accounts`, {
        params: { accountName: request.accountName ? request.accountName : undefined }
      });
      return response.data;
    },

    savePlexTranslations: async (translations: IPlexTranslationMessageSaveRequest[]) => {
      const response = await getClientFor(mainApi.name).post("/messages/translations", translations);
      return response.data;
    },

    exportPlexTranslationsToXml: async (parameters: IPlexTranslationMessagesGetRequest) => {
      const params = {
        locale: parameters.locale,
        industry: parameters.industry,
        text: parameters.text,
        messageId: parameters.messageId,
        status: parameters.status,
        parent: parameters.parent
      };

      await getClientFor(mainApi.name).get("/messages/plex-translations/export", { params, responseType: "blob" });
    },

    approvePlexTranslations: async (request: IPlexTranslationsApproveRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/approve`, request.translations);
      return response.data;
    },

    approvePlexTranslationsWithIndustry: async (request: IPlexTranslationsApproveRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/industries/${request.industry}/approve`, request.translations);
      return response.data;
    },

    requestPlexTranslations: async (request: IPlexTranslationsRequestRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/request`, request.translations);
      return response.data;
    },

    requestPlexTranslationsWithIndustry: async (request: IPlexTranslationsRequestRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/industries/${request.industry}/request`, request.translations);
      return response.data;
    },

    reassignPlexTranslations: async (request: IPlexTranslationsReassignRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/reassign`, request.translations);
      return response.data;
    },

    reassignPlexTranslationsWithIndustry: async (request: IPlexTranslationsReassignRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/industries/${request.industry}/reassign`, request.translations);
      return response.data;
    },

    reviewPlexTranslations: async (request: IPlexTranslationsReviewRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/review`, request.translations);
      return response.data;
    },

    reviewPlexTranslationsWithIndustry: async (request: IPlexTranslationsReviewRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/industries/${request.industry}/review`, request.translations);
      return response.data;
    },

    proposePlexTranslations: async (request: IPlexTranslationsProposeRequest) => {
      const response = await getClientFor(mainApi.name).post(`/messages/plex-translations/propose`, request.translations);
      return response.data;
    },

    rejectPlexTranslations: async (request: IPlexTranslationsRejectRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/reject`, request.translations);
      return response.data;
    },

    rejectPlexTranslationsWithIndustry: async (request: IPlexTranslationsRejectRequest) => {
      const response = await getClientFor(mainApi.name).post(`messages/plex-translations/locales/${request.locale}/industries/${request.industry}/reject`, request.translations);
      return response.data;
    },

    obsoletePlexTranslations: async (request: IPlexTranslationsObsoleteRequest) => {
      const response = await getClientFor(mainApi.name).delete(`messages/plex-translations/locales/${request.locale}`, { data: request.translations });
      return response.data;
    },

    obsoleteIndustryTranslations: async (request: IPlexTranslationsObsoleteRequest) => {
      const response = await getClientFor(mainApi.name).delete(`messages/plex-translations/locales/${request.locale}/industries/${request.industry}`, {
        data: request.translations
      });
      return response.data;
    },

    downloadXmlTemplate: async () => await getClientFor(mainApi.name).get("/messages/plex-translations/template", { responseType: "blob" })
  };
};

export const plexTranslationMessagesManagerApi = getApiProvider();
